import React, { FC } from 'react';
import { Box, Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack, TextField } from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import { Recipient } from '@models';
import { setRecipient } from '@store/slices/order';

const schema = yup.object({
  type: yup.string().required(),
  first_name: yup.string().when('type', {
    is: 'SomeoneElse',
    then: yup.string().required('First name is required.'),
  }),
  last_name: yup.string().when('type', {
    is: 'SomeoneElse',
    then: yup.string().required('Last name is required.'),
  }),
  phone: yup.string().when('type', {
    is: 'SomeoneElse',
    then: yup.string().required('Phone number is required.'),
  }),
});

type Props = {
  handleBack: () => void;
  handleNext: () => void;
};

export const RecipientSelection: FC<Props> = ({ handleBack, handleNext }) => {
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Recipient>({
    resolver: yupResolver(schema),
  });

  const customer = useAppSelector((state) => state.customer.value.selectedCustomer);
  const recipient = useAppSelector((state) => state.order.value.recipient);

  const dispatch = useAppDispatch();

  const onSubmit: SubmitHandler<Recipient> = async (newRecipient: Recipient) => {
    if (newRecipient.type === 'OnlyCustomer') {
      dispatch(
        setRecipient({
          type: 'OnlyCustomer',
          first_name: customer?.first_name,
          last_name: customer?.last_name,
          phone: customer?.phone,
        }),
      );
    }

    if (newRecipient.type === 'AnyAdult') {
      dispatch(setRecipient({ type: 'AnyAdult' }));
    }

    if (newRecipient.type === 'SomeoneElse') {
      dispatch(setRecipient(newRecipient));
    }

    handleNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2} marginLeft={1} marginTop={2}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Who can receive this order?</FormLabel>
          <Controller
            control={control}
            defaultValue={recipient.type}
            name={'type'}
            render={({ field }) => (
              <RadioGroup row {...field}>
                <FormControlLabel control={<Radio />} label="Only the customer" value={'OnlyCustomer'} data-testid="customer-only-radio" />
                <FormControlLabel control={<Radio />} label="Any adult at the address" value={'AnyAdult'} data-testid="any-adult-radio" />
                <FormControlLabel control={<Radio />} label="Someone else" value={'SomeoneElse'} data-testid="someone-radio" />
              </RadioGroup>
            )}
          />
        </FormControl>
        {watch().type === 'SomeoneElse' && (
          <>
            <TextField
              {...register('first_name')}
              error={!!errors.first_name}
              helperText={errors.first_name?.message}
              label="First Name *"
              sx={{ maxWidth: 240 }}
              data-testid="input-firstname"
            />
            <TextField
              {...register('last_name')}
              error={!!errors.last_name}
              helperText={errors.last_name?.message}
              label="Last Name *"
              sx={{ maxWidth: 240 }}
              data-testid="input-lastname"
            />
            <TextField
              {...register('phone')}
              error={!!errors.phone}
              helperText={errors.phone?.message}
              label="Phone *"
              sx={{ maxWidth: 240 }}
              data-testid="input-phone"
            />
          </>
        )}
      </Stack>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
        <Button onClick={handleBack} sx={{ mr: 1 }} data-testid="recipient-back-btn">
          Back
        </Button>
        <Button type="submit" data-testid="recipient-next-btn">
          Next
        </Button>
      </Box>
    </form>
  );
};
